<template>
  <div class="canvas-tool">
    <div class="add-element-handler">

        <div class="handler-item" :class="{ 'disable': !canUndo }" @click.stop="undo()"><IconBack /></div>
        <div class="handler-item" :class="{ 'disable': !canRedo }" @click.stop="redo()"><IconNext /></div>

      <div>
        <IconLayers class="handler-item" v-tooltip.right="{ value: 'Layers Panel', showDelay: 500, hideDelay: 100 }" @click="toggleLayerPanel()"/>
      </div>

      <div>
        <IconGraphicDesign
            v-tooltip.bottom="{ value: 'Shape', showDelay: 500, hideDelay: 100 }"
            class="handler-item"
            @click="$refs.shapePoolVisible.toggle($event)"
            :class="{ 'active': creatingElement?.type === 'shape' }"/>
        <OverlayPanel ref="shapePoolVisible">
          <ShapePool @click="$refs.shapePoolVisible.hide()" @select="shape => drawShape(shape)"/>
        </OverlayPanel>
      </div>

      <div>
        <IconConnection
            v-tooltip.bottom="{ value: 'Line', showDelay: 500, hideDelay: 100 }"
            class="handler-item"
            @click="$refs.linePoolVisible.toggle($event)" :class="{ 'active': creatingElement?.type === 'line' }"/>
        <OverlayPanel ref="linePoolVisible">
          <LinePool @click="$refs.linePoolVisible.hide()" @select="line => drawLine(line)"/>
        </OverlayPanel>
      </div>

      <div>
        <IconDisplay class="handler-item" v-tooltip="'Section Container'"
          @click="drawSection('bar')"
        />
        <IconConnection
            v-tooltip.bottom="{ value: 'Line', showDelay: 500, hideDelay: 100 }"
            class="handler-item"
            @click="$refs.linePoolVisible.toggle($event)" :class="{ 'active': creatingElement?.type === 'line' }"/>
        <OverlayPanel ref="linePoolVisible">
          <LinePool @click="$refs.linePoolVisible.hide()" @select="line => drawLine(line)"/>
        </OverlayPanel>
      </div>



      <div class="conditional-element-handler" v-if="(activeWidget === 'image' && selectedWidget.customerImageButton) || ['reg_text', 'model_data', 'csv_selector', 'button', 'table'].includes(activeWidget)" style="display:flex;">
        <TextSettings></TextSettings>
      </div>
      <div v-if="activeWidget === 'table'|| activeWidget === 'model_data'" style="display:flex; margin-top: 7px">
          <ValueModelTextAutoComplete ></ValueModelTextAutoComplete>
      </div>


    </div>



    <Modal
        v-model:visible="latexEditorVisible"
        :footer="null"
        centered
        :width="880"
        destroyOnClose
    >
      <LaTeXEditor
          @close="latexEditorVisible = false"
          @update="data => { createLatexElement(data); latexEditorVisible = false }"
      />
    </Modal>

    <Sidebar v-model:visible="hotkeyDrawerVisible" header="Sidebar">
      <HotkeyDoc />
    </Sidebar>
  </div>
</template>

<script lang="ts" setup>
import {ref, watch, onMounted, computed} from 'vue'
import {storeToRefs} from 'pinia'
import {useGivingTreeStore, useMainStore, useSlidesStore, useSnapshotStore, useWidgetStore} from '@/store'
import {ShapePoolItem} from '@/configs/shapes'
import {LinePoolItem} from '@/configs/lines'
import useScaleCanvas from '@/hooks/useScaleCanvas'
import useCreateElement from '@/hooks/useCreateElement'
import ShapePool from './ShapePool.vue'
import LinePool from './LinePool.vue'
import LaTeXEditor from '@/components/LaTeXEditor/index.vue'
import {Modal} from 'ant-design-vue'
import ValueModelTextAutoComplete from './components/ValueModelTextAutoComplete.vue'
import TextSettings from "./components/TextSettings.vue";
import HotkeyDoc from '../EditorHeader/HotkeyDoc.vue'
import { WEB_FONTS } from '@/configs/font'
import useMicroservice from "@/hooks/useMicroservice.ts";
import {PresetChartType} from "@/types/slides";
import useHistorySnapshot from "@/hooks/useHistorySnapshot";


const mainStore = useMainStore()
const { showSelectPanel } = storeToRefs(mainStore)
const widgetStore = useWidgetStore()
const activeWidget = ref('none')
const { activeType, selectedWidget } = storeToRefs(widgetStore)
const { canUndo, canRedo, snapshotCursor } = storeToRefs(useSnapshotStore())
const { redo, undo } = useHistorySnapshot()


const {creatingElement} = storeToRefs(mainStore)


const {
  setCanvasScalePercentage,
} = useScaleCanvas()

const canvasScalePresetList = [200, 150, 100, 80, 50]
const canvasScaleVisible = ref(false)


const slidesStore = useSlidesStore()
const {currentSlide} = storeToRefs(slidesStore)


watch(currentSlide, () => {
  widgetStore.setActiveWidgets([])
}, { immediate: true })



const hotkeyDrawerVisible = ref(false)

onMounted(() => {
  const slide = currentSlide.value.elements.find(item => item.type === 'dragdrop')
  if (slide) {
    const font = WEB_FONTS.find(item => item.value === slide.defaultFontName)
    selectFont.value = font ? font : 'Arial'
  }
})

const applyCanvasPresetScale = (value: number) => {
  setCanvasScalePercentage(value)
  canvasScaleVisible.value = false
}

const setMargin = computed(() => {
  if (activeType.value !== 'none' && activeType.value !== 'csv_selector' &&  activeType.value !== 'image') {
    return 10 + 'px'
  } else {
    return 0
  }
})

const setFlex = computed(() => {
  if (activeType.value !== 'none' && activeType.value !== 'csv_selector' &&  activeType.value !== 'image') {
    return 'column'
  } else {
    return ''
  }
})

const setBorder = computed(() => {
  if (activeType.value === 'none') {
    return `1px solid #eee`
  } else {
    return 0
  }
})

const setTop = computed(() => {
  return 50 + '%'

})

const {
  createLatexElement,
} = useCreateElement()

/**
 * Commenting out for now
 */
// const insertImageElement = (files: FileList) => {
//   const imageFile = files[0]
//   if (!imageFile) return
//   getImageDataURL(imageFile).then(dataURL => createImageElement(dataURL))
// }

watch(() => activeType,
    (newActiveType) => {
      activeWidget.value = newActiveType.value
    }, { deep: true })


const selectFont = ref()
const shapePoolVisible = ref()
const linePoolVisible = ref()
const latexEditorVisible = ref(false)
const dataEditVisible = ref(false)
const vmDataVisible = ref()
const dragDropVisible = ref()
const slideLayoutVisible = ref()
const formatNumberVisible = ref()

const { savePresentation, getLibrarySlideElements } = useMicroservice()


watch(selectFont, async (newVal) => {
  const el = currentSlide.value.elements.find(item => item.type === 'dragdrop')
  slidesStore.updateElement({ id: el.id, props: { defaultFontName: newVal.value }})
  await savePresentation()
})

// draw text range
/**
 * Commenting out for now
 */
// const drawText = (vertical = false) => {
//   mainStore.setCreatingElement({
//     type: 'text',
//     vertical,
//   })
// }

// draw shape range
const drawShape = (shape: ShapePoolItem) => {
  mainStore.setCreatingElement({
    type: 'shape',
    data: shape,
  })
}


const drawSection = (type: PresetChartType) => {
  useCreateElement().createSectionElement(type)
}

// draw line path
const drawLine = (line: LinePoolItem) => {
  mainStore.setCreatingElement({
    type: 'line',
    data: line,
  })
}

const toggleLayerPanel = () => {
  if (!showSelectPanel.value) mainStore.setSelectPanelState(true)
  else mainStore.setSelectPanelState(false)
}

</script>

<style lang="scss" scoped>
:deep(.p-button){
  background: #fff;
  border: 1px solid #ccc;
  color: black;
}

.vertical-line {
  border-left: 0.5px solid #e3e3e3; /* Adjust color and thickness as needed */
  height: 20px; /* Adjust height as needed */
  margin-left: 10px; /* Adjust margin as needed */
  margin-right: 10px; /* Adjust margin as needed */
}

.autocomplete-item:hover {
  background-color: #d7d7d7;
}

.canvas-tool {
  position: relative;
  border-bottom: v-bind(setBorder);
  background-color: #fff;
  display: flex;
  justify-content: space-between;
  padding: 0 10px;
  font-size: 13px;
  user-select: none;
}

.add-element-handler {

  display: flex;

  .handler-item {
    width: 32px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 4px 2px;
    border-radius: $borderRadius;

    &:not(.group-btn):hover {
      background-color: #f9f9f9;
    }

    &.active {
      color: $themeColor;
    }

    &.group-btn {
      width: auto;
      margin-right: 4px;

      &:hover {
        background-color: #f3f3f3;
      }

      .icon, .arrow {
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .icon {
        width: 26px;
        padding: 0 2px;

        &:hover {
          background-color: #e9e9e9;
        }

        &.active {
          color: $themeColor;
        }
      }

      .arrow {
        font-size: 12px;

        &:hover {
          background-color: #e9e9e9;
        }
        .canvas-tool {
          overflow: hidden;
          position: relative;
          border-bottom: v-bind(setBorder);
          background-color: #fff;
          display: flex;
          justify-content: space-between;
          padding: 0 10px;
          font-size: 13px;
          user-select: none;
        }
      }
    }
  }
}


.horizontal-line {
  border-top: 0.5px solid #e3e3e3;
  width: 100%;
  margin-top: 2px;
  margin-bottom: 2px;
}

.handler-item {
  margin: 0 10px;
  font-size: 14px;
  overflow: hidden;
  cursor: pointer;

  &.disable {
    opacity: .5;
    pointer-events: none; /* Prevent interactions with the element */
    filter: grayscale(100%); /* Optionally, you can grayscale the icon */
  }
}

.right-handler {
  display: flex;
  align-items: center;

  .text {
    width: 32px;
    text-align: center;
    cursor: pointer;
  }

  .viewport-size {
    font-size: 13px;
  }
}

.preset-item {
  padding: 8px 20px;
  text-align: center;
  cursor: pointer;

  &:hover {
    color: $themeColor;
  }
}

.text-type-item {
  padding: 5px 10px;
  cursor: pointer;
  margin-bottom: 0px;

  &:hover {
    background-color: #f9f9f9;
  }

  & + .text-type-item {
    margin-top: 3px;
  }
}

@media screen and (max-width: 1024px) {
  .text {
    display: none;
  }
  .handler-item {
    margin: 0 5px;
  }
}

@media screen and (max-width: 1300px) {
  .right-handler {
    display: none;
  }
}

@media screen and (max-width: 1100px) {
  .left-handler, .right-handler {
    display: none;
  }
}


.column-block {
  background: #a7a7a7;
  font-size: 11px;
  text-align: center;
  text-indent: -9999em;
  padding: 2px 0;
  -webkit-transition: background .2s ease;
  transition: background .2s ease;
  border-radius: 3px;
  height: 15px;
  margin: 0 2px 3px;
}

.dragDropOverlay {
  width: 165px;
  height: auto;
}

.slideLayoutOverlay {
  width: 165px;
  height: 300px;
}

.p-menubar.p-component.subMenuBar {
  padding: 0;
  background: inherit;
  color: inherit;
  border: 0;
  border-radius: 0;
  &:hover {
    background-color: #f3f3f3;
  }
}

.p-menubar.p-component.subMenuBar > .p-menubar-root-list {
  font-weight: 300;
}

.p-dropdown.p-component.p-inputwrapper.p-inputwrapper-filled.fonts-dropdown,
.p-dropdown.p-component.p-inputwrapper.fonts-dropdown {
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(.p-dropdown-label) {
  font-size: 12px;
}

:deep(.p-dropdown-trigger) {
  font-size: 12px;
}

.fonts-dropdown.handler-item {
  width: 100px;
}

:deep(.p-dropdown-panel.p-component) {
  font-size: 12px;
}
.vertical-line {
  border-left: 0.5px solid #e3e3e3; /* Adjust color and thickness as needed */
  height: 20px; /* Adjust height as needed */
  margin-left: 10px; /* Adjust margin as needed */
  margin-right: 10px; /* Adjust margin as needed */
}

:deep(.font-incrementer > .p-inputtext) {
  text-align: center;
  width: 50px;
}

.font-incrementer > .p-button {
  border: none;
}

.settings-list {
  @include flex-grid-layout();
}

:deep(.p-dropdown.p-component.p-inputwrapper.p-inputwrapper-filled.preset-dropdown,
.p-dropdown.p-component.p-inputwrapper.preset-dropdown)
{
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(.preset-dropdown > .p-dropdown-label) {
  font-size: 12px
}

:deep(.preset-dropdown > .p-dropdown-trigger) {
  font-size: 12px;
}

:deep(.preset-dropdown.handler-item) {
  width: 100px;
}

:deep(.p-autocomplete-panel .p-autocomplete-items .p-autocomplete-item-group) {
  background: #f1f5f9;
}
:deep(.auto-dropdown) {
  height: 20px;
}

:deep(.p-autocomplete-dd .p-autocomplete-input) {
  height: 20px;
  font-size: 12px;
}

:deep(.p-button.p-component.p-button-icon-only.p-autocomplete-dropdown.auto-dropdown) {
  border-left: none;
  border-color: rgb(206, 212, 218);
}

:deep(.p-button.p-component.p-button-icon-only.p-autocomplete-dropdown.auto-dropdown > .p-icon) {
  width: 12px;
}

</style>
